
import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Stethoscope, Eye, EyeOff, Mail, Lock } from 'lucide-react';

export default function TherapistLogin() {
  const { login, isAuthenticated, user } = useAuth();
  const [, navigate] = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });

  useEffect(() => {
    // Redirect if already authenticated as therapist
    if (isAuthenticated && user) {
      if (user.role === 'therapist') {
        navigate('/therapist-dashboard');
      } else if (user.role === 'admin') {
        navigate('/admin');
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(loginData.email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    // Sanitize email
    const sanitizedEmail = loginData.email.trim().toLowerCase();

    // Pre-check if email exists as therapist user
    try {
      const checkResponse = await fetch('/api/auth/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: sanitizedEmail,
          userType: 'therapist'
        })
      });

      const checkData = await checkResponse.json();

      if (!checkData.exists) {
        setError('No therapist account found with this email. Please check your credentials.');
        setIsLoading(false);
        return;
      }

      if (!checkData.hasPassword) {
        setError('This therapist account uses OAuth. Please contact support.');
        setIsLoading(false);
        return;
      }
    } catch (checkError) {
      console.error('Email pre-check failed:', checkError);
      // Continue with login attempt even if pre-check fails
    }

    const result = await login(sanitizedEmail, loginData.password, rememberMe);

    if (result.success) {
      // Check if user is therapist
      const storedUser = localStorage.getItem('auth_user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        if (userData.role === 'therapist') {
          navigate('/therapist-dashboard');
        } else if (userData.role === 'admin') {
          navigate('/admin');
        } else {
          // Client users should not use this login page
          setError('Access denied. This login is for therapist accounts only. Please use the client login page.');
          // Log them out
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
        }
      }
    } else {
      setError(result.error || 'Invalid credentials');
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-900 via-teal-800 to-teal-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Stethoscope className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-extrabold text-white">
            Therapist Portal
          </h2>
          <p className="mt-2 text-sm text-teal-200">
            Professional Access Portal
          </p>
        </div>

        <Card className="shadow-2xl border-teal-700">
          <CardHeader className="bg-teal-800 text-white rounded-t-lg">
            <CardTitle>Therapist Login</CardTitle>
            <CardDescription className="text-teal-200">
              Enter your therapist credentials to continue
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {error && (
              <Alert className="mb-4" variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="therapist-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="therapist-email"
                    type="email"
                    placeholder="therapist@healthyowl.in"
                    className="pl-10"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="therapist-password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="therapist-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Your password"
                    className="pl-10 pr-10"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex items-center space-x-2 mb-4">
                <input
                  type="checkbox"
                  id="remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <label
                  htmlFor="remember-me"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Remember me
                </label>
              </div>

              <Button 
                type="submit" 
                className="w-full !bg-teal-600 hover:!bg-teal-700 !text-white font-semibold py-3 h-12" 
                disabled={isLoading}
              >
                {isLoading ? 'Authenticating...' : 'Sign In as Therapist'}
              </Button>
            </form>

            <div className="mt-4 text-center space-y-2">
              <Button
                variant="link"
                className="text-sm text-teal-600 hover:text-teal-700 w-full"
                onClick={() => navigate('/forgot-password')}
              >
                Forgot Password?
              </Button>
              <Button
                variant="link"
                className="text-sm text-gray-600 hover:text-gray-900"
                onClick={() => navigate('/')}
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <p className="text-xs text-teal-300">
            This is a secure therapist area. All access attempts are logged.
          </p>
        </div>
      </div>
    </div>
  );
}
